import { uploadFileWithProgress } from "../../../../packages/app-ui/js/uploadWithProgress.js";
import { extractTranscriptText } from "../utils/transcriptUtils.js";
import {
  stripExtension,
  findDuplicate,
  generateUniqueName,
} from "../utils/fileUtils.js";
import { preflightMediaFile } from "../utils/mediaPreflight.js";
import { showDuplicatePrompt } from "../../../../packages/app-ui/js/confirmModal.js";

function inferLanguageHint(file) {
  return file?.type?.startsWith("es") ? "es" : null;
}

export function createUploadController({
  fileInput,
  dropzone,
  globalDropTarget = typeof window !== "undefined" ? window : null,
  jobsStore,
  backendAdapter,
  isOverQuota,
  onQuotaBlocked,
  onJobsUpdated,
}) {
  const activeUploads = new Map();

  function bindInputs() {
    if (fileInput) {
      fileInput.addEventListener("change", (event) => {
        const files = event.target.files;
        if (files && files.length > 0) {
          Array.from(files).forEach((file) => queueUpload(file));
        }
        fileInput.value = "";
      });
    }
    if (dropzone) {
      dropzone.addEventListener("dragover", (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropzone.classList.add("drag-active");
      });
      dropzone.addEventListener("dragleave", (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropzone.classList.remove("drag-active");
      });
      dropzone.addEventListener("drop", (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropzone.classList.remove("drag-active");
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
          Array.from(files).forEach((file) => queueUpload(file));
        }
      });
    }
    bindGlobalDropTarget(globalDropTarget);
  }

  async function queueUpload(file, options = {}) {
    if (!file) return;

    // block upload if over quota
    if (typeof isOverQuota === "function" && isOverQuota()) {
      notifyUpdate({
        type: "upload-rejected",
        filename: file.name,
        reason: "quota_exceeded",
      });
      notifyUpdate({ type: "quota-refresh" });
      return;
    }

    let displayName = stripExtension(file.name);

    // preflight size/type/duration (required for server-side validation + quota holds)
    const preflight = await preflightMediaFile(file);
    if (!preflight.ok) {
      notifyUpdate({
        type: "upload-rejected",
        filename: file.name,
        reason: preflight.reason || "unknown",
      });
      return;
    }
    const durationSec = preflight.durationSec;

    // check for duplicates unless explicitly skipped
    if (!options.skipDuplicateCheck) {
      const duplicate = await findDuplicate(file, jobsStore.files);
      if (duplicate) {
        const choice = await showDuplicatePrompt(
          duplicate.existing.displayName,
          duplicate.confidence,
        );

        if (choice === "cancel") {
          return;
        }

        if (choice === "replace") {
          // delete the existing file first, then proceed with upload
          const existingId = duplicate.existing.jobId || duplicate.existing.id;
          if (existingId && typeof backendAdapter.deleteJob === "function") {
            try {
              await backendAdapter.deleteJob(existingId);
            } catch {
              // if delete fails, proceed anyway - server might not have the job yet
            }
          }
          jobsStore.removeFileById(duplicate.existing.id);
          notifyUpdate();
        }

        if (choice === "new") {
          // generate unique name like mac: "name (1)", "name (2)", etc.
          const existingNames = jobsStore.files.map((f) => f.displayName);
          displayName = generateUniqueName(displayName, existingNames);
        }
      }
    }

    const entry = jobsStore.createPendingFile({
      filename: displayName,
      sizeBytes: file.size,
    });
    jobsStore.attachLocalFile(entry, file);
    entry.status = "uploading";
    notifyUpdate({ type: "upload-queued", entry });
    notifyUpdate();
    try {
      const uploadSession = await backendAdapter.createUpload({
        filename: file.name,
        size_bytes: file.size,
        duration_sec: durationSec,
        mime_type: file.type || "application/octet-stream",
        title: displayName,
        language_hint: inferLanguageHint(file),
      });
      await performUpload(entry, file, uploadSession);
      await createJobFromUpload(entry, uploadSession);
    } catch (error) {
      console.error("upload_failed", error);

      // ignore aborts (canceled uploads)
      if (isAbortError(error)) {
        return;
      }

      // check for invalid file type error from worker
      const isInvalidType =
        error &&
        typeof error === "object" &&
        error.status === 400 &&
        error.payload?.code === "invalid_payload" &&
        error.payload?.message === "file type not recognized as audio or video";

      if (isInvalidType) {
        // show toast and remove entry without creating a failed row
        notifyUpdate({
          type: "upload-rejected",
          filename: file.name,
          reason: "invalid_type",
        });
        jobsStore.removeFileById(entry.id);
        return;
      }

      const isTooLarge =
        error &&
        typeof error === "object" &&
        error.status === 413 &&
        error.payload?.code === "file_too_large";
      if (isTooLarge) {
        notifyUpdate({
          type: "upload-rejected",
          filename: file.name,
          reason: "file_too_large",
        });
        jobsStore.removeFileById(entry.id);
        return;
      }

      const isTooLong =
        error &&
        typeof error === "object" &&
        error.status === 413 &&
        error.payload?.code === "duration_too_long";
      if (isTooLong) {
        notifyUpdate({
          type: "upload-rejected",
          filename: file.name,
          reason: "duration_too_long",
        });
        jobsStore.removeFileById(entry.id);
        return;
      }

      const isDurationRequired =
        error &&
        typeof error === "object" &&
        error.status === 400 &&
        error.payload?.code === "duration_required";
      if (isDurationRequired) {
        notifyUpdate({
          type: "upload-rejected",
          filename: file.name,
          reason: "duration_unreadable",
        });
        jobsStore.removeFileById(entry.id);
        return;
      }

      // check for quota exceeded (429)
      const isQuotaExceeded =
        error &&
        typeof error === "object" &&
        error.status === 429 &&
        error.payload?.code === "quota_exceeded";

      if (isQuotaExceeded) {
        notifyUpdate({
          type: "upload-rejected",
          filename: file.name,
          reason: "quota_exceeded",
        });
        notifyUpdate({ type: "quota-refresh" });
        jobsStore.removeFileById(entry.id);
        return;
      }

      // generic failure: keep the failed row
      entry.status = "failed";
      entry.failureReason =
        error instanceof Error ? error.message : "upload failed";
      entry.progress = 0;
      notifyUpdate();
    }
  }

  async function performUpload(entry, file, uploadSession) {
    const controller = new AbortController();
    activeUploads.set(entry.id, controller);
    try {
      await uploadFileWithProgress({
        url: uploadSession.upload_url,
        file,
        headers: uploadSession.headers,
        method: "PUT",
        onProgress: (progress) => {
          entry.progress = Math.round(progress * 100);
          entry.status = progress >= 1 ? "transcribing" : "uploading";
          notifyUpdate({ type: "upload-progress", entry });
        },
        signal: controller.signal,
      });
    } finally {
      activeUploads.delete(entry.id);
    }
  }

  async function createJobFromUpload(entry, uploadSession) {
    entry.status = "transcribing";
    entry.progress = 5;
    notifyUpdate();
    try {
      const job = await backendAdapter.createJob({
        media_r2_key: uploadSession.media_r2_key,
        title: entry.displayName,
        mode: "long",
        async: true,
      });

      jobsStore.linkPendingToJob(entry, job);
      notifyUpdate({ type: "job-linked" });
      notifyUpdate({ type: "quota-refresh" });
      notifyUpdate({ type: "server-refresh" });
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        error.status === 429 &&
        error.payload?.code === "quota_exceeded"
      ) {
        notifyUpdate({
          type: "upload-rejected",
          filename: entry.displayName,
          reason: "quota_exceeded",
        });
        notifyUpdate({ type: "quota-refresh" });
        jobsStore.removeFileById(entry.id);
        return;
      }
      throw error;
    }
  }

  function notifyUpdate(event) {
    if (typeof onJobsUpdated === "function") {
      onJobsUpdated(event || null);
    }
  }

  function cancelFile(id) {
    const controller = activeUploads.get(id);
    if (controller) {
      controller.abort();
      activeUploads.delete(id);
      jobsStore.removeFileById(id);
      notifyUpdate();
    }
  }

  function retryFile(id) {
    const entry = jobsStore.files.find((file) => file.id === id);
    if (!entry || !entry.localFile) {
      return;
    }
    jobsStore.removeFileById(id);
    notifyUpdate();
    queueUpload(entry.localFile);
  }

  async function downloadFile(id) {
    const entry =
      jobsStore.files.find((file) => file.id === id) ||
      jobsStore.findByJobId(id);
    if (!entry) {
      return;
    }
    const content = await loadTranscriptForEntry(entry);
    const blob = new Blob([content], { type: "text/plain" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${entry.displayName || entry.name || "transcript"}.txt`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
      link.remove();
    }, 0);
  }

  async function loadTranscriptForEntry(entry) {
    if (entry.transcriptR2Key && entry.jobId) {
      const payload = await backendAdapter.getTranscript(entry.jobId);
      return extractTranscriptText(payload?.transcript);
    }
    return entry.preview_snippet || "transcript not ready yet";
  }

  bindInputs();
  function bindGlobalDropTarget(target) {
    if (!target || !target.addEventListener) {
      return;
    }
    let dragDepth = 0;
    const isFileDrag = (event) => {
      const types = event?.dataTransfer?.types;
      if (!types) return false;
      return Array.from(types).includes("Files");
    };

    target.addEventListener("dragenter", (event) => {
      if (!isFileDrag(event)) return;
      dragDepth += 1;
      document.body.classList.add("dragging-file");
    });
    target.addEventListener("dragover", (event) => {
      if (!isFileDrag(event)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    });
    target.addEventListener("dragleave", (event) => {
      if (!isFileDrag(event)) return;
      dragDepth = Math.max(0, dragDepth - 1);
      if (dragDepth === 0) {
        document.body.classList.remove("dragging-file");
      }
    });
    target.addEventListener("drop", (event) => {
      const isFile = isFileDrag(event);
      if (isFile) {
        event.preventDefault();
      }
      document.body.classList.remove("dragging-file");
      dragDepth = 0;
      if (!isFile) return;
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        Array.from(files).forEach((file) => queueUpload(file));
      }
    });
    target.addEventListener("dragend", () => {
      dragDepth = 0;
      document.body.classList.remove("dragging-file");
    });
    target.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        dragDepth = 0;
        document.body.classList.remove("dragging-file");
      }
    });
  }

  return {
    cancelFile,
    retryFile,
    downloadFile,
  };
}

function isAbortError(error) {
  if (!error) return false;
  if (typeof error === "object" && error.name === "AbortError") return true;
  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase().includes("abort");
}
